import { OWDataType } from "@multiweb/types/contracts";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "Global/Base/Rights/RightTypes";
import { EnumUtils } from "Global/Base/Utils/EnumUtils";
import { Event as TEvent } from "../../../../Global/Base/Events";
import { EnumType, EnumWithDataType } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { IEnumDescriptionWithDataType } from "../../../../OW.Remoting/Remoting/DataTypes/IEnums";
import { ChartType } from "../../../UI.Controls/ChartElement";
import { IChartColumnDefinition } from "../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ChartDisplay } from "../Controls/ChartDisplay";
import { ChartHorizontalAxis } from "../Controls/ChartHorizontalAxis";
import { ChartVerticalAxis } from "../Controls/ChartVerticalAxis";
import { GenericListFrame } from "../XGControls/Frames/GenericListFrame";
import { ChartSettingsHeaderBuilder } from "./ChartSettingsHeaderBuilder";
import { IContentBuilder } from "./IContentBuilder";
/**
 * třída představuje tělo ChartSettingsPanelu
 * obsahuje sekce Vodorovná osa, Svislá osa, Zobrazení
 */
export class ChartSettingsContentBuilder implements IContentBuilder {
    /** událost změny agregační funkce */
    public aggregationFunctionChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost změny názvu kategorie */
    public captionChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost změny atributu osy y */
    public attrNameChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost kliku na tlačítko smazat */
    public deleteDatasetClicked: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost změny xAxisAttr */
    public xAxisAttrChanged: TEvent<string, void> = new TEvent<string, void>();
    /** událost změny typu grafu */
    public chartTypeChanged: TEvent<ChartType, void> = new TEvent<ChartType, void>();
    /** událost změny orientace grafu */
    public chartOrientationChanged: TEvent<boolean, void> = new TEvent<boolean, void>();
    /** událost zobrazení / skrytí legendy */
    public showLegendChanged: TEvent<boolean, void> = new TEvent<boolean, void>();
    private _element: HTMLElement;
    private _masterFrame: GenericListFrame;
    private _datasets: IChartColumnDefinition[];
    /** data pro combobox */
    private _comboBoxData: EnumType;
    /** data pro combobox bez dat datetime, date, time */
    private _comboBoxDataWithNoDate: EnumType;
    /** sekce Vodorovná osa */
    private _horizontalAxis: ChartHorizontalAxis;
    /** sekce Svislá osa */
    private _verticalAxis: ChartVerticalAxis;
    /** sekce Zobrazení */
    private _chartDisplay: ChartDisplay;
    private _headerBuilder: ChartSettingsHeaderBuilder;
    private _xAxisAttr: string;
    private _chartType: ChartType;
    private _showLegend: boolean;
    private _cssClass: string = "chart-settings-content-builder";

    constructor(masterFrame: GenericListFrame, datasets: IChartColumnDefinition[], xAxisAttr: string, chartType: ChartType, showLegend: boolean, headerBuilder: ChartSettingsHeaderBuilder) {
        this._masterFrame = masterFrame;
        this._datasets = datasets;
        this._xAxisAttr = xAxisAttr;
        this._chartType = chartType;
        this._showLegend = showLegend;
        this._buildComboBoxData();
        this._headerBuilder = headerBuilder;
        this._element = createElement({ classes: [this._cssClass] });

        // horizontal axis
        this._horizontalAxis = new ChartHorizontalAxis(this._xAxisAttr, this._comboBoxData);
        this._horizontalAxis.xAxisAttrChanged.add(value => this.xAxisAttrChanged.trigger(this.checkComboBoxValue(value), null));

        //vertical axis
        this._verticalAxis = new ChartVerticalAxis({
            element: createElement(),
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
            masterFrame: this._masterFrame,
            datasets: this._datasets,
            comboBoxData: this._comboBoxDataWithNoDate,
            chartType: this._chartType,
        });
        this._verticalAxis.aggregationFunctionChanged.add(value => this._runChartSettingsManagerEventsWithDataset(value, this.aggregationFunctionChanged));
        this._verticalAxis.captionChanged.add(value => this._runChartSettingsManagerEventsWithDataset(value, this.captionChanged));
        this._verticalAxis.attrNameChanged.add(value => this._runChartSettingsManagerEventsWithDataset(this.checkComboBoxValueArray(value), this.attrNameChanged));
        this._verticalAxis.deleteDatasetClicked.add(value => this._runChartSettingsManagerEventsWithDataset(value, this.deleteDatasetClicked));

        //display
        this._chartDisplay = new ChartDisplay(this._chartType, this._showLegend, this._headerBuilder);
        this._chartDisplay.chartTypeChanged.add(value => this.chartTypeChanged.trigger(value, null));
        this._chartDisplay.chartOrientationChanged.add(value => this.chartOrientationChanged.trigger(value, null));
        this._chartDisplay.showLegendChanged.add(value => this.showLegendChanged.trigger(value, null));

        this._element.appendChild(this._horizontalAxis.element);
        this._element.appendChild(this._verticalAxis.element);
        this._element.appendChild(this._chartDisplay.element);
    }

    private _runChartSettingsManagerEventsWithDataset(value: string[], chartSettingsManagerEvent: TEvent<string[], void>): void {
        chartSettingsManagerEvent.trigger(value, null);
    }

    /**
     * vytvoří data pro comboboxy vodorovné a svislé osy
     * vytváří se seznam dat obsahující i datum - _comboBoxData
     * nebo v seznamu dat datum není - _comboBoxDataWithNoDate
     */
    private _buildComboBoxData(): void {
        const comboBoxDesc: IEnumDescriptionWithDataType[] = [];
        const comboBoxDescNoDate: IEnumDescriptionWithDataType[] = [];
        let i = 0;
        let y = 0;
        this._masterFrame.dataGrid.columns.forEach(function(item) {
            if ([
                OWDataType.binary,
                OWDataType.color,
                OWDataType.flags,
                OWDataType.htmlText,
                OWDataType.icon,
                OWDataType.document,
                OWDataType.OWObject,
                OWDataType.OWObjectList,
                OWDataType.text,
            ].indexOf(EnumUtils.getEnumValueFromName(OWDataType, item.dataType)) < 0) {
                comboBoxDesc.push({ IntValue: i, Name: item.attrName, LocKey: item.locKey, LocParams: item.locParams, Description: item.columnName, DataType: item.dataType });
                if ([
                    OWDataType.date,
                    OWDataType.time,
                    OWDataType.dateTime,
                ].indexOf(EnumUtils.getEnumValueFromName(OWDataType, item.dataType)) < 0) {
                    comboBoxDescNoDate.push({ IntValue: y, Name: item.attrName, LocKey: item.locKey, LocParams: item.locParams, Description: item.columnName, DataType: item.dataType });
                    y++;
                }
                i++;
            }
        });
        this._comboBoxData = new EnumType(comboBoxDesc, "");
        this._comboBoxDataWithNoDate = new EnumType(comboBoxDescNoDate, "");
    }

    private checkComboBoxValueArray(values: string[]): string[] {
        values[0] = this.checkComboBoxValue(values[0]);
        return values;
    }

    private checkComboBoxValue(comboBoxValue: string): string {
        const enumCombo = this._comboBoxData.getValue(comboBoxValue);
        if (enumCombo != null) {
            if ((enumCombo as EnumWithDataType).getDataType() == OWDataType.localizedText) {
                return comboBoxValue + ".text";
            }
        }
        return comboBoxValue;
    }

    private _initializeListeners(): void {
        this._headerBuilder.chartTypeChanged.add(chartType => {
            this._verticalAxis.setChartType(chartType);
        });
    }

    public async build(): Promise<HTMLElement> {
        this._initializeListeners();

        return this._element;
    }

    public dispose(): void {
        this._horizontalAxis.dispose();
        this._verticalAxis.dispose();
        this._chartDisplay.dispose();
    }
}
